// THE PERSISTENT LANDED-SOURCES PANEL — every golden source landed for this period, with the
// per-source attest control.
//
// WHY IT EXISTS. Attestation was only ever offered from the transient "what was landed" panel
// inside CombinedCsvDropZone, which renders off local `results` state. Reload the page — as
// happens between sessions — and every attest button on the tab disappeared. Eight sources were
// landed for 2026-07 (measured 2026-08-12: all eight non-superseded, every one ingest_status
// 'parsed' and attested_by NULL) and not one of them could be attested through the UI, while
// reporting_snapshot_precheck listed all four required kinds as unattested and held the cycle
// shut. A control that only exists in the seconds after an upload is not a control.
//
// WHAT THIS PANEL DELIBERATELY DOES NOT DO
//
//   * It does not offer a VALIDATE action, because there is no supported path to press. The
//     precheck treats attestation and validation as two separate states
//     (unattested_required vs unvalidated_required) and computes `validated` as
//     ingest_status = 'validated'. NOTHING in the estate writes that value: both ingest
//     functions stop at 'parsed' (reporting-golden-ingest/index.ts:3225,
//     reporting-supabase-ingest/index.ts:1323), the only other statuses they write are
//     'dropped' and 'rejected', and of the three live functions that mention
//     reporting_golden_sources at all only reporting_snapshot_precheck contains the literal
//     'validated' — it READS it. So validation is not a side effect of a pipeline stage that
//     a human simply has to wait for; it is unimplemented. Inventing a raw UPDATE here would
//     be a client writing its own gate result, which is the exact failure the golden-source
//     model exists to prevent. The panel states the gap instead, and says what would close it.
//
//   * It does not write attested_by. attest_golden_source takes the actor from auth.uid()
//     server-side; the column additionally carries a FK to auth.users and a CHECK binding it
//     to attested_at.
//
// ON THE NOTE FIELD. attest_golden_source DOES take a note — the live signature is
// attest_golden_source(_source_id uuid, _note text DEFAULT NULL) — and it is the only place an
// attester's own words can be recorded. It lands in reporting_approvals.note, NOT in
// reporting_golden_sources.attestation_note (the RPC never touches that column; the ingest
// writes it, to describe the SOURCE). reporting_approvals is append-only, so a note is
// permanent and uncorrectable — which is why this panel refuses to send an empty one and makes
// the actor say what they are vouching for before the button does anything.

import { useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Info,
  Loader2,
  ShieldCheck,
  ShieldQuestion,
} from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useReportingAccess } from "@/hooks/investment-reporting/useReportingAccess";
import {
  useAttestGoldenSource,
  useGoldenSources,
  useSnapshotPrecheck,
  useSourceAttestations,
  type GoldenSourceRow,
  type SourceAttestation,
} from "@/hooks/investment-reporting/useGoldenSources";
import { formatDay, goldenKindLabel, shortHash } from "./labels";

// A signature needs the MINUTE, not just the day — formatDay is date-only, which is too coarse
// to distinguish an attestation from the run that preceded it. Mirrors ContentReviewReceipt's
// formatStamp, the island's existing way of stamping an approvals-ledger entry.
function formatStamp(t: string | null | undefined): string {
  if (!t) return "—";
  const dt = new Date(t);
  if (Number.isNaN(dt.getTime())) return String(t);
  return dt.toLocaleString();
}

// ---------------------------------------------------------------------------
// Provenance, in the words an attester needs.
//
// Derived from the ROW, not from a hardcoded per-kind table: source_system is constrained by
// reporting_golden_sources_source_class_chk to a closed allow-matrix, and original_filename
// carries the native extract's descriptor. A source_system this file does not recognise still
// renders — as its raw token — rather than being given a friendly label that might be wrong.
// ---------------------------------------------------------------------------
interface Provenance {
  origin: string;
  /** What the identity hash is taken over — the thing the attester is actually binding to. */
  identity: string;
  /** Source tables for a native extract, parsed off the synthetic descriptor. */
  tables: string[];
  /** Extraction timestamp for a native extract (ISO), if the descriptor carries one. */
  extractedAt: string | null;
  /** Base date for a point-in-time reconstruction, if the descriptor carries one. */
  baseDate: string | null;
}

export function describeProvenance(row: GoldenSourceRow): Provenance {
  const fn = row.original_filename ?? "";
  const native = fn.startsWith("supabase:");

  // "supabase:ba_trades+ba_r_trades@2026-08-11T15:40:37.746Z"
  // "supabase:ba_cbam_fund_weights+ba_r_cbam_fund_weights@base=2026-06-30@2026-08-12T13:33:02.400Z"
  let tables: string[] = [];
  let extractedAt: string | null = null;
  let baseDate: string | null = null;
  if (native) {
    const body = fn.slice("supabase:".length);
    const segments = body.split("@");
    tables = (segments[0] ?? "").split("+").filter(Boolean);
    for (const seg of segments.slice(1)) {
      if (seg.startsWith("base=")) baseDate = seg.slice("base=".length);
      else if (!extractedAt) extractedAt = seg;
    }
  }

  switch (row.source_system) {
    case "fe_quarter_end_csv":
      return {
        origin: "An FE export file, uploaded by a human.",
        identity:
          "The hash below is taken over the bytes of the uploaded file itself, so it identifies " +
          "exactly the export that was dropped.",
        tables,
        extractedAt,
        baseDate,
      };
    case "ba_blotter_attested":
      return {
        origin: "Derived from the BA rebalance blotter held in this database — there was no file.",
        identity:
          "The hash is taken over a deterministic CSV rendered from the query result, not over " +
          "an uploaded file. It identifies the extract, and it will only reproduce if the " +
          "underlying blotter rows are unchanged.",
        tables,
        extractedAt,
        baseDate,
      };
    case "ba_weights_attested":
      return {
        origin:
          "Derived from the BA append-only effective-dated weights history in this database — " +
          "there was no file. Provenance is a QUERY, not a document.",
        identity:
          "The hash is taken over a deterministic CSV rendered from the point-in-time query, " +
          "not over an uploaded file. It reproduces only if the winning rows are unchanged.",
        tables,
        extractedAt,
        baseDate,
      };
    case "supabase_static_ref":
      return {
        origin: "A static reference table in this database.",
        identity: "The hash is taken over a deterministic CSV rendered from that table.",
        tables,
        extractedAt,
        baseDate,
      };
    default:
      return {
        origin: `Source system "${row.source_system}" — not a system this panel has a description for.`,
        identity: "The hash is taken over whatever bytes the ingest recorded for this source.",
        tables,
        extractedAt,
        baseDate,
      };
  }
}

// The two honest bases for an attestation, offered as presets so the distinction between them
// is made explicitly rather than being buried in whatever the actor happens to type.
//
// The second is REQUIRED to be verbatim: the CIO has authorised attestation to be applied
// without the actor personally re-deriving the numbers, and an audit record that does not say
// so would misrepresent what was checked. reporting_approvals is append-only, so this wording
// is the permanent statement of the basis on which the source was signed.
const CIO_STANDING_AUTHORISATION_NOTE =
  "Applied under the CIO's standing authorisation for automated attestation. " +
  "The named actor did NOT personally re-derive or re-inspect the underlying data. " +
  "This attestation rests on the CIO's standing authorisation and on the ingest-side gates " +
  "recorded in this source's own provenance (content hash, row count, source system), " +
  "not on an independent human review of the numbers.";

const PERSONAL_REVIEW_PREFIX =
  "I have reviewed this source and I stand behind it as the golden input for this period. ";

interface AttestDraft {
  note: string;
  /** Which preset last populated the note, for the radio-ish highlight. */
  basis: "personal" | "cio" | "custom";
}

export interface GoldenSourcesPanelProps {
  periodLabel: string | null | undefined;
  cycleId?: string | null;
  className?: string;
}

export function GoldenSourcesPanel({ periodLabel, cycleId, className }: GoldenSourcesPanelProps) {
  const { user } = useAuth();
  const access = useReportingAccess();
  const sources = useGoldenSources(periodLabel, access.canReview);
  const precheck = useSnapshotPrecheck(periodLabel, access.canReview);
  // The ledger is what carries WHO signed and on what basis — the source row carries only the
  // uuid and the timestamp, and nothing at all about the note.
  const ledger = useSourceAttestations(cycleId, access.canReview);
  const attest = useAttestGoldenSource(periodLabel, cycleId);

  const [expanded, setExpanded] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, AttestDraft>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  // required_kinds comes from the server. If the precheck has not loaded (or errored) the
  // panel says it cannot tell required from extra, rather than falling back to a guess — a
  // wrong "required" badge would tell someone to attest the wrong thing.
  const requiredKinds = precheck.data?.required_kinds ?? null;
  const requiredKnown = Array.isArray(requiredKinds);

  const rows = sources.data ?? [];

  const { required, extra } = useMemo(() => {
    if (!requiredKnown) return { required: [] as GoldenSourceRow[], extra: rows };
    const req = new Set(requiredKinds as string[]);
    return {
      required: rows.filter((r) => req.has(r.kind)),
      extra: rows.filter((r) => !req.has(r.kind)),
    };
  }, [rows, requiredKinds, requiredKnown]);

  // Required kinds with NO row at all. The precheck reports these as missing_required; showing
  // them here keeps the panel a complete account of the required set rather than a list of
  // what happens to have landed.
  const missingKinds: string[] = useMemo(() => {
    if (!requiredKnown) return [];
    const present = new Set(rows.map((r) => r.kind));
    return (requiredKinds as string[]).filter((k) => !present.has(k));
  }, [rows, requiredKinds, requiredKnown]);

  if (access.loading || !access.canReview) return null;

  function draftFor(id: string): AttestDraft {
    return drafts[id] ?? { note: "", basis: "custom" };
  }

  function setDraft(id: string, next: Partial<AttestDraft>) {
    setDrafts((prev) => ({ ...prev, [id]: { ...draftFor(id), ...next } }));
  }

  async function submitAttest(row: GoldenSourceRow) {
    const draft = draftFor(row.id);
    setErrors((prev) => ({ ...prev, [row.id]: "" }));
    try {
      await attest.mutateAsync({ sourceId: row.id, note: draft.note });
      setExpanded(null);
    } catch (e: any) {
      setErrors((prev) => ({ ...prev, [row.id]: e?.message ?? String(e) }));
    }
  }

  function renderRow(row: GoldenSourceRow, isRequired: boolean) {
    const prov = describeProvenance(row);
    const attested = Boolean(row.attested_by);
    const byYou = attested && user?.id && row.attested_by === user.id;
    const isOpen = expanded === row.id;
    const draft = draftFor(row.id);
    const busy = attest.isPending && attest.variables?.sourceId === row.id;
    const err = errors[row.id];
    // The signature's basis, from the append-only ledger. Newest first; more than one means the
    // row was re-attested and the earlier signature was overwritten on the row itself.
    const entries: SourceAttestation[] = ledger.data?.get(row.id) ?? [];
    const latest: SourceAttestation | null = entries[0] ?? null;
    // The precheck's own definition, mirrored exactly: validated means ingest_status =
    // 'validated'. Nothing writes it, so this is false on every row today — which is the
    // point, and why it is stated rather than hidden.
    const validated = row.ingest_status === "validated";

    return (
      <li key={row.id} className="rounded-md border bg-card">
        {/* ── Summary line ─────────────────────────────────────────────────── */}
        <div className="p-3 space-y-1.5">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0 space-y-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-body text-sm font-medium text-foreground">
                  {goldenKindLabel(row.kind)}
                </span>
                {isRequired ? (
                  <span className="font-body text-[10px] uppercase tracking-wide rounded px-1.5 py-0.5 bg-primary/10 text-primary">
                    Required
                  </span>
                ) : (
                  <span className="font-body text-[10px] uppercase tracking-wide rounded px-1.5 py-0.5 bg-muted text-muted-foreground">
                    Extra
                  </span>
                )}
              </div>
              <p className="font-mono text-[11px] text-muted-foreground break-all">
                {row.kind} · {row.source_system} · {row.row_count ?? "—"} rows · sha{" "}
                {shortHash(row.content_sha256)}
              </p>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              {attested ? (
                <span className="inline-flex items-center gap-1 font-body text-[11px] text-primary">
                  <ShieldCheck className="h-3.5 w-3.5" aria-hidden />
                  {byYou ? "Attested by you" : "Attested"}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 font-body text-[11px] text-amber-600 dark:text-amber-400">
                  <ShieldQuestion className="h-3.5 w-3.5" aria-hidden />
                  Not attested
                </span>
              )}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-[11px] text-muted-foreground"
                onClick={() => setExpanded(isOpen ? null : row.id)}
                aria-expanded={isOpen}
              >
                {isOpen ? (
                  <ChevronDown className="h-3.5 w-3.5 mr-1" aria-hidden />
                ) : (
                  <ChevronRight className="h-3.5 w-3.5 mr-1" aria-hidden />
                )}
                {attested ? "Details" : "Review and attest"}
              </Button>
            </div>
          </div>

          {/* State line: the two gate states, side by side and separately named. */}
          <div className="flex items-center gap-3 flex-wrap font-body text-[11px]">
            <span className="text-muted-foreground">
              Ingest status:{" "}
              <span
                className={cn(
                  "font-medium",
                  row.ingest_status === "rejected" ? "text-destructive" : "text-foreground",
                )}
              >
                {row.ingest_status}
              </span>
            </span>
            <span className="text-muted-foreground">
              Validated:{" "}
              <span className={cn("font-medium", validated ? "text-primary" : "text-amber-600 dark:text-amber-400")}>
                {validated ? "yes" : "no"}
              </span>
            </span>
            <span className="text-muted-foreground">
              Attested:{" "}
              <span className={cn("font-medium", attested ? "text-primary" : "text-amber-600 dark:text-amber-400")}>
                {attested ? formatStamp(row.attested_at) : "no"}
              </span>
            </span>
            {attested && (
              <span className="text-muted-foreground">
                By:{" "}
                <span className="font-medium text-foreground">
                  {byYou ? "you" : latest?.actor_team ? latest.actor_team : "—"}
                </span>{" "}
                <span className="font-mono">{shortHash(row.attested_by, 8)}</span>
              </span>
            )}
            <span className="text-muted-foreground">Landed {formatDay(row.created_at)}</span>
          </div>

          {row.ingest_status === "rejected" && row.reject_reason && (
            <p className="font-body text-[11px] text-destructive break-words">
              Rejected at ingest: {row.reject_reason}
            </p>
          )}
        </div>

        {/* ── Expanded: what you would be signing for ───────────────────────── */}
        {isOpen && (
          <div className="border-t p-3 space-y-3 bg-muted/20">
            <div className="space-y-1.5">
              <h5 className="font-heading text-xs font-medium text-foreground">
                What this source is, and where it came from
              </h5>
              <p className="font-body text-[11px] text-muted-foreground">{prov.origin}</p>
              <p className="font-body text-[11px] text-muted-foreground">{prov.identity}</p>

              <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 font-body text-[11px] pt-1">
                <dt className="text-muted-foreground">Content hash</dt>
                <dd className="font-mono text-foreground break-all">{row.content_sha256}</dd>

                {prov.tables.length > 0 ? (
                  <>
                    <dt className="text-muted-foreground">Source tables</dt>
                    <dd className="font-mono text-foreground break-all">{prov.tables.join(", ")}</dd>
                  </>
                ) : (
                  <>
                    <dt className="text-muted-foreground">File</dt>
                    <dd className="text-foreground break-all">
                      {row.original_filename ?? (
                        <span className="text-muted-foreground">
                          No filename recorded on this row
                        </span>
                      )}
                    </dd>
                  </>
                )}

                {prov.baseDate && (
                  <>
                    <dt className="text-muted-foreground">Base date</dt>
                    <dd className="font-mono text-foreground">{prov.baseDate}</dd>
                  </>
                )}
                {prov.extractedAt && (
                  <>
                    <dt className="text-muted-foreground">Extracted at</dt>
                    <dd className="font-mono text-foreground break-all">{prov.extractedAt}</dd>
                  </>
                )}

                <dt className="text-muted-foreground">Rows</dt>
                <dd className="text-foreground">{row.row_count ?? "—"}</dd>
                <dt className="text-muted-foreground">Basis</dt>
                <dd className="text-foreground">{row.basis}</dd>
                <dt className="text-muted-foreground">As of</dt>
                <dd className="text-foreground">{formatDay(row.as_of_date)}</dd>
              </dl>
            </div>

            {/* The ingest's own caveat. Named for what it IS — the column is called
                attestation_note but no attester wrote it. */}
            {row.attestation_note && (
              <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-2.5 space-y-1">
                <p className="font-body text-[11px] font-medium text-foreground flex items-center gap-1.5">
                  <Info className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400 shrink-0" aria-hidden />
                  Caveat recorded by the ingest (not by an attester)
                </p>
                <p className="font-body text-[11px] text-muted-foreground whitespace-pre-wrap break-words">
                  {row.attestation_note}
                </p>
              </div>
            )}

            {/* ── The attest control ─────────────────────────────────────────── */}
            {attested ? (
              <div className="border-t pt-2.5 space-y-2">
                <h5 className="font-heading text-xs font-medium text-foreground">
                  This source has been attested
                </h5>
                <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 font-body text-[11px]">
                  <dt className="text-muted-foreground">Attested by</dt>
                  <dd className="text-foreground break-all">
                    {byYou ? "You" : latest?.actor_team ? `${latest.actor_team} team` : "Actor"}{" "}
                    <span className="font-mono text-muted-foreground">
                      ({shortHash(row.attested_by, 12)})
                    </span>
                  </dd>
                  <dt className="text-muted-foreground">Attested at</dt>
                  <dd className="text-foreground">{formatStamp(row.attested_at)}</dd>
                </dl>

                {/* The BASIS — the whole point of the audit record. It lives in
                    reporting_approvals.note, never on the source row, so it has to be read back
                    from the ledger. */}
                {latest?.note ? (
                  <div className="rounded-md border bg-card p-2.5 space-y-1">
                    <p className="font-body text-[11px] font-medium text-foreground">
                      What the attester vouched for, verbatim from the approvals ledger
                    </p>
                    <p className="font-body text-[11px] text-muted-foreground whitespace-pre-wrap break-words">
                      {latest.note}
                    </p>
                    <p className="font-body text-[10px] text-muted-foreground">
                      Recorded {formatStamp(latest.created_at)} in{" "}
                      <span className="font-mono">reporting_approvals</span>, which is append-only
                      and cannot be edited or deleted.
                    </p>
                  </div>
                ) : ledger.isLoading ? (
                  <p className="font-body text-[11px] text-muted-foreground">
                    Reading the approvals ledger for the stated basis…
                  </p>
                ) : (
                  <p className="font-body text-[11px] text-amber-600 dark:text-amber-400">
                    No stated basis was recorded with this attestation. The signature exists but
                    the ledger entry carries no note, so the audit record does not say what was
                    checked.
                  </p>
                )}

                {entries.length > 1 && (
                  <p className="font-body text-[11px] text-amber-600 dark:text-amber-400">
                    This source has been attested {entries.length} times. The row shows only the
                    most recent signature — the RPC overwrites{" "}
                    <span className="font-mono">attested_by</span>/
                    <span className="font-mono">attested_at</span> rather than refusing — so the
                    earlier signatures survive only in the ledger.
                  </p>
                )}

                <p className="font-body text-[11px] text-muted-foreground">
                  Attesting again is not offered here. The RPC would not refuse it: its UPDATE is
                  unconditional and a second attestation would REPLACE the name and timestamp
                  above, leaving the original recoverable only from the ledger.
                </p>
              </div>
            ) : (
              <div className="border-t pt-2.5 space-y-2">
                <div className="rounded-md border border-destructive/30 bg-destructive/5 p-2.5">
                  <p className="font-body text-[11px] text-foreground flex items-start gap-1.5">
                    <AlertTriangle className="h-3.5 w-3.5 text-destructive shrink-0 mt-0.5" aria-hidden />
                    <span>
                      Attesting is vouching. You are recording, permanently and under your own
                      name, that this source is fit to be the golden input every number in the{" "}
                      {periodLabel} report traces back to. The approvals ledger cannot be edited
                      or deleted afterwards.
                    </span>
                  </p>
                </div>

                <div className="space-y-1.5">
                  <p className="font-body text-[11px] font-medium text-foreground">
                    On what basis are you attesting?
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant={draft.basis === "personal" ? "default" : "outline"}
                      size="sm"
                      className="h-7 px-2 text-[11px]"
                      onClick={() =>
                        setDraft(row.id, { basis: "personal", note: PERSONAL_REVIEW_PREFIX })
                      }
                    >
                      I have reviewed it myself
                    </Button>
                    <Button
                      type="button"
                      variant={draft.basis === "cio" ? "default" : "outline"}
                      size="sm"
                      className="h-7 px-2 text-[11px]"
                      onClick={() =>
                        setDraft(row.id, { basis: "cio", note: CIO_STANDING_AUTHORISATION_NOTE })
                      }
                    >
                      Under the CIO&rsquo;s standing authorisation
                    </Button>
                  </div>
                  {draft.basis === "cio" && (
                    <p className="font-body text-[11px] text-amber-600 dark:text-amber-400">
                      This wording is recorded verbatim in the audit record, and it states plainly
                      that nobody re-derived the numbers. Leave it intact unless it is untrue.
                    </p>
                  )}
                </div>

                <div className="space-y-1">
                  <label
                    htmlFor={`attest-note-${row.id}`}
                    className="font-body text-[11px] text-muted-foreground"
                  >
                    What you are vouching for — recorded verbatim in the approvals ledger, and
                    required.
                  </label>
                  <Textarea
                    id={`attest-note-${row.id}`}
                    value={draft.note}
                    onChange={(e) =>
                      setDraft(row.id, {
                        note: e.target.value,
                        basis: draft.basis === "custom" ? "custom" : draft.basis,
                      })
                    }
                    rows={4}
                    className="font-body text-[11px]"
                    placeholder="Say what you checked, or on whose authority you are signing. This is the permanent audit record."
                  />
                </div>

                {err && (
                  <p className="font-body text-[11px] text-destructive break-words">{err}</p>
                )}

                <div className="flex items-center gap-2 flex-wrap">
                  <Button
                    type="button"
                    size="sm"
                    className="h-7 px-2 text-[11px]"
                    disabled={busy || !draft.note.trim()}
                    onClick={() => void submitAttest(row)}
                  >
                    {busy ? (
                      <>
                        <Loader2 className="h-3 w-3 mr-1 animate-spin" aria-hidden />
                        Attesting…
                      </>
                    ) : (
                      <>
                        <ShieldCheck className="h-3 w-3 mr-1" aria-hidden />
                        Attest this source
                      </>
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-[11px] text-muted-foreground"
                    onClick={() => setExpanded(null)}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </li>
    );
  }

  return (
    <section className={cn("space-y-2", className)}>
      <h3 className="font-heading text-base font-medium text-foreground">
        Landed sources for this period
      </h3>
      <p className="font-body text-xs text-muted-foreground max-w-3xl">
        Everything recorded against{" "}
        <span className="font-mono">{periodLabel ?? "this period"}</span> in{" "}
        <span className="font-mono">reporting_golden_sources</span>, read back from the server —
        so it is here after a reload, on another laptop, and for whoever picks the cycle up next.
        Which kinds are <span className="font-medium text-foreground">required</span> is taken
        from <span className="font-mono">reporting_snapshot_precheck</span>, which derives them
        from the cycle&rsquo;s period type; this panel does not keep its own list.
      </p>

      {sources.isLoading && (
        <Card className="p-4">
          <p className="font-body text-xs text-muted-foreground">Reading the landed sources…</p>
        </Card>
      )}

      {sources.isError && (
        <Card className="p-4">
          <p className="font-body text-xs text-destructive break-words">
            Could not read the landed sources:{" "}
            {(sources.error as any)?.message ?? "unknown error"}. No attest control is offered
            while the list is unknown.
          </p>
        </Card>
      )}

      {!sources.isLoading && !sources.isError && rows.length === 0 && (
        <Card className="p-4">
          <p className="font-body text-xs text-muted-foreground">
            Nothing has been landed for this period yet. Drop the combined export above.
          </p>
        </Card>
      )}

      {rows.length > 0 && (
        <>
          {precheck.isError && (
            <Card className="p-3">
              <p className="font-body text-[11px] text-amber-600 dark:text-amber-400 break-words">
                The snapshot precheck could not be read (
                {(precheck.error as any)?.message ?? "unknown error"}), so required and extra
                sources cannot be told apart below. Everything landed is listed, unlabelled.
              </p>
            </Card>
          )}

          {requiredKnown && (
            <ul className="space-y-2">{required.map((r) => renderRow(r, true))}</ul>
          )}

          {/* Required kinds that have not landed at all. */}
          {missingKinds.length > 0 && (
            <Card className="p-3 space-y-1">
              <p className="font-body text-[11px] font-medium text-foreground">
                Required, but nothing landed yet
              </p>
              <ul className="space-y-0.5">
                {missingKinds.map((k) => (
                  <li key={k} className="font-body text-[11px] text-amber-600 dark:text-amber-400">
                    {goldenKindLabel(k)} <span className="font-mono text-muted-foreground">({k})</span>
                  </li>
                ))}
              </ul>
            </Card>
          )}

          {(requiredKnown ? extra : rows).length > 0 && (
            <div className="space-y-2 pt-1">
              <p className="font-body text-xs text-muted-foreground">
                {requiredKnown
                  ? "Also landed — not required for this period type, and not blocking the gate."
                  : "Landed sources (required/extra unknown — see above)."}
              </p>
              <ul className="space-y-2">
                {(requiredKnown ? extra : rows).map((r) => renderRow(r, false))}
              </ul>
            </div>
          )}

          {/* ── The validation gap, and the ruling that closed it. ─────────────── */}
          <Card className="p-3 space-y-1.5 border-border bg-muted/30">
            <p className="font-body text-[11px] font-medium text-foreground flex items-center gap-1.5">
              <Info className="h-3.5 w-3.5 text-muted-foreground shrink-0" aria-hidden />
              There is no validate action, because attestation is the control
            </p>
            <p className="font-body text-[11px] text-muted-foreground">
              The precheck used to require each required kind to be BOTH attested and validated,
              defining validated as{" "}
              <span className="font-mono">ingest_status = 'validated'</span>. No code path in the
              estate has ever written that value: both ingest functions stop at{" "}
              <span className="font-mono">'parsed'</span>, and the only other statuses they write
              are <span className="font-mono">'dropped'</span> and{" "}
              <span className="font-mono">'rejected'</span>. Validation was not a later pipeline
              stage waiting its turn. It was unimplemented, so{" "}
              <span className="font-mono">ready</span> could never become true and no cycle could
              ever pass the snapshot gate.
            </p>
            <p className="font-body text-[11px] text-muted-foreground">
              Resolved by CIO ruling on 12 August 2026 and applied on 13 August: one human act,
              not two. Attesting a source IS vouching for it, and a second undefined act would
              become a rubber stamp, which is worse than no control because it looks like one.
              The <span className="font-mono">all_validated</span> term has been dropped from the{" "}
              <span className="font-mono">ready</span> predicate.{" "}
              <span className="font-mono">unvalidated_required</span> is still reported below, but
              it is informational only and no longer gates anything. Attesting every required
              source here is now sufficient to make the snapshot stage pass.
            </p>
          </Card>

          {/* The live precheck verdict, so the panel and the gate cannot disagree. */}
          {precheck.data && (
            <Card className="p-3 space-y-1">
              <p className="font-body text-[11px] font-medium text-foreground">
                Snapshot precheck, live
              </p>
              <p className="font-body text-[11px] text-muted-foreground">
                Period type <span className="font-mono">{precheck.data.period_type}</span> ·
                ready{" "}
                <span
                  className={cn(
                    "font-medium",
                    precheck.data.ready ? "text-primary" : "text-amber-600 dark:text-amber-400",
                  )}
                >
                  {precheck.data.ready ? "yes" : "no"}
                </span>
              </p>
              {precheck.data.unattested_required?.length > 0 && (
                <p className="font-body text-[11px] text-muted-foreground break-words">
                  Unattested:{" "}
                  <span className="font-mono">
                    {precheck.data.unattested_required.join(", ")}
                  </span>
                </p>
              )}
              {precheck.data.unvalidated_required?.length > 0 && (
                <p className="font-body text-[11px] text-muted-foreground break-words">
                  Unvalidated (informational, does not gate):{" "}
                  <span className="font-mono">
                    {precheck.data.unvalidated_required.join(", ")}
                  </span>
                </p>
              )}
            </Card>
          )}
        </>
      )}
    </section>
  );
}
